import type { Dispatch, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { FILE_TIMEOUT_MS, resourceTag, transportRequest } from '@krp/client-runtime'
import { withImageType } from '@krp/ui-kit'

import { onecEnvelopeDecoder } from '../../../shared/api/onecEnvelope'
import { newOperationId } from '../../../shared/model/operationIds'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import type { PhotoPortOutcome, PhotosPorts } from '../model/ports'
import { mainPhotoOutcome } from '../model/outcomes'
import { decodeUploadResult, type UploadOutcome } from './contracts'
import { ONEC_PHOTO_TAG, onecSetMainPhoto, photosApi } from './photosApi'
import { ONEC_PHOTO_URLS } from './urls'

type ThunkCapable = ThunkDispatch<unknown, unknown, UnknownAction>

/**
 * Загрузка снимка идёт multipart общего transport (RP-AU1a) командой
 * `transportRequest` мимо мутации RTK: аргументы мутации RTK хранит в Redux, а
 * `File` туда класть нельзя. Повтора нет, обрыв — неизвестный исход.
 */
export function createPhotosPorts(dispatch: Dispatch<UnknownAction>): PhotosPorts {
  const run = dispatch as ThunkCapable
  return {
    newOperationId,
    now: () => new Date().toISOString(),
    setMain: async (operation): Promise<PhotoPortOutcome> => {
      const started = run(
        onecSetMainPhoto.initiate({
          resourceId: operation.resourceId,
          refKey: operation.refKey,
          fileKey: operation.fileKey,
          identity: {
            operationId: operation.operationId,
            idempotencyKey: operation.idempotencyKey,
            expectedConfigurationVersion: operation.expectedConfigurationVersion,
          },
        }),
      )
      try {
        const answer = await started.unwrap()
        return { ok: true, outcome: mainPhotoOutcome(answer.data) }
      } catch (error) {
        return { ok: false, error }
      } finally {
        started.reset()
      }
    },
    upload: async (operation, file, observer): Promise<PhotoPortOutcome> => {
      // Поля формы — по договору службы (PhotoEndpoints): file и три поля команды.
      const formData = new FormData()
      // Файл уходит как выбран, без перекодирования; пустой тип (HEIC в Chrome на Windows) получает тип по расширению — сервер требует image/*.
      const named = file instanceof File ? file : new File([file], operation.fileName ?? 'photo', { type: file.type })
      const typed = withImageType(named)
      formData.append('file', typed, typed.name)
      formData.append('operationId', operation.operationId)
      formData.append('idempotencyKey', operation.idempotencyKey)
      formData.append('expectedConfigurationVersion', operation.expectedConfigurationVersion)
      const result = await run(
        transportRequest<{ data: { outcome: UploadOutcome } }>(
          {
            url: ONEC_PHOTO_URLS.upload(operation.resourceId, operation.refKey),
            method: 'POST',
            formData,
            idempotencyKey: operation.idempotencyKey,
            timeoutMs: FILE_TIMEOUT_MS,
            decode: onecEnvelopeDecoder(operation.resourceId, decodeUploadResult),
          },
          observer?.signal,
          // Прогресс — реальные байты из XMLHttpRequest.upload; без xhr (тесты) наблюдатель не вызывается, и числа не выдумываются.
          observer?.onProgress === undefined ? undefined : { onUploadProgress: observer.onProgress },
        ),
      )
      return result.ok ? { ok: true, outcome: result.data.data.outcome } : { ok: false, error: result.error }
    },
    reload: (resourceId, refKey) => {
      dispatch(photosApi.util.invalidateTags([resourceTag(ONEC_PHOTO_TAG, normalizeRid(resourceId), refKey)]))
    },
  }
}
